import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Undo as UndoIcon,
  Edit as EditIcon,
  Lock as LockIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonOff as PersonOffIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDraftAssignments,
  postPlan,
  discardDraft,
  rerunAutoAssignment,
  updateAssignment,
  removeAssignment,
  recallAndRedraft,
  getAuditLog,
} from '@timetable/api';
import type { DraftAssignment } from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '../Toast';
import { queryKeys } from '../../lib/queryKeys';
import { ConfirmDialog } from '../ConfirmDialog';
import { TeacherSelector } from './TeacherSelector';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DraftReviewPanelProps {
  absenceId: string;
  targetDate: string;
  absentTeacherName: string;
  absenceType: 'full_day' | 'temporary_duty';
  absenceStatus: string;
  onClose?: () => void;
}

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: string;
  severity: 'info' | 'warning' | 'error';
  onConfirm: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(time: string): string {
  return time.slice(0, 5); // HH:MM
}

function getDayOfWeek(isoDate: string): string {
  const date = parseISO(isoDate);
  return format(date, 'EEEE'); // e.g., "Monday"
}

function formatDate(isoDate: string): string {
  const date = parseISO(isoDate);
  return format(date, 'MMMM d, yyyy'); // e.g., "April 15, 2025"
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DraftReviewPanel({
  absenceId,
  targetDate,
  absentTeacherName,
  absenceType,
  absenceStatus,
  onClose,
}: DraftReviewPanelProps) {
  const queryClient = useQueryClient();
  const { schoolContext, user } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();

  // UI state
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    title: '',
    message: '',
    severity: 'info',
    onConfirm: () => {},
  });
  const [auditLogExpanded, setAuditLogExpanded] = useState(false);
  const [newlyUnassignedPeriodIds, setNewlyUnassignedPeriodIds] = useState<Set<string>>(
    new Set()
  );
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);
  const [assigningPeriodId, setAssigningPeriodId] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const isStandardUser = user?.role === 'standard';

  // Fetch draft assignments
  const {
    data: assignments,
    isLoading: loadingAssignments,
    error: assignmentsError,
  } = useQuery({
    queryKey: queryKeys.assignments.list({ absence_id: absenceId, date: targetDate }),
    queryFn: () => getDraftAssignments(absenceId, targetDate),
    enabled: !!absenceId && !!targetDate,
  });

  // Fetch audit log (Admin only)
  const { data: auditLog } = useQuery({
    queryKey: ['audit-log', absenceId],
    queryFn: () => getAuditLog(absenceId),
    enabled: isAdmin && !!absenceId,
  });

  // Derive status from assignments
  const isDraft = assignments?.some((a) => a.status === 'draft') ?? false;
  const isPosted = assignments?.some((a) => a.status === 'posted') ?? false;
  const allPosted = assignments?.every((a) => a.status === 'posted') ?? false;

  const assignedAssignments = assignments?.filter((a) => a.substitute_teacher_id !== null) ?? [];
  const unassignedAssignments = assignments?.filter((a) => a.substitute_teacher_id === null) ?? [];

  const totalPeriods = assignments?.length ?? 0;
  const assignedCount = assignedAssignments.length;
  const unassignedCount = unassignedAssignments.length;

  // Check for deactivated substitutes (Req 3.19, 3.20)
  const hasDeactivatedSubstitutes = assignedAssignments.some(
    (a) => a.substitute_teacher && !a.substitute_teacher.is_active
  );

  // Fetch absences on targetDate to detect substitutes who are themselves absent
  const { data: absencesOnDate } = useQuery({
    queryKey: ['absences-on-date', targetDate, schoolContext],
    queryFn: async () => {
      const { getAbsences } = await import('@timetable/api');
      return getAbsences({
        school_id: schoolContext || '',
        status: 'all',
        page: 1,
        page_size: 200,
      });
    },
    enabled: !!targetDate && !!schoolContext,
    select: (data) =>
      new Set(
        data
          .filter(
            (a) =>
              a.start_date <= targetDate &&
              a.end_date >= targetDate &&
              a.status !== 'cancelled'
          )
          .map((a) => a.teacher_id)
      ),
  });

  const absentTeacherIds: Set<string> = absencesOnDate ?? new Set();

  const hasAbsentSubstitutes = assignedAssignments.some(
    (a) => a.substitute_teacher_id && absentTeacherIds.has(a.substitute_teacher_id)
  );

  const canPost = isDraft && !isStandardUser;

  // ── Mutations ─────────────────────────────────────────────────────────────

  const updateAssignmentMutation = useMutation({
    mutationFn: ({ id, substituteTeacherId }: { id: string; substituteTeacherId: string }) =>
      updateAssignment(id, substituteTeacherId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      showSuccess('Assignment updated successfully');
      setEditingAssignmentId(null);
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to update assignment');
    },
  });

  const removeAssignmentMutation = useMutation({
    mutationFn: (id: string) => removeAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      showSuccess('Assignment removed');
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to remove assignment');
    },
  });

  const postPlanMutation = useMutation({
    mutationFn: () => postPlan(absenceId, targetDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      showSuccess('Plan posted successfully');
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to post plan');
    },
  });

  const discardDraftMutation = useMutation({
    mutationFn: () => discardDraft(absenceId, targetDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      showSuccess('Draft discarded');
      onClose?.();
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to discard draft');
    },
  });

  const rerunMutation = useMutation({
    mutationFn: () => rerunAutoAssignment(absenceId, targetDate),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });

      // Highlight newly unassigned periods (Req 5.7)
      const previousAssignedIds = new Set(
        assignedAssignments.map((a) => a.period_id)
      );
      const newAssignedIds = new Set(
        result.assignments
          .filter((a) => a.substitute_teacher_id !== null)
          .map((a) => a.period_id)
      );
      const newlyUnassigned = [...previousAssignedIds].filter(
        (id) => !newAssignedIds.has(id)
      );
      setNewlyUnassignedPeriodIds(new Set(newlyUnassigned));

      showSuccess('Auto-assignment re-run complete');
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to re-run auto-assignment');
    },
  });

  const recallMutation = useMutation({
    mutationFn: () => recallAndRedraft(absenceId, targetDate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      showSuccess('Plan recalled and reverted to draft');
    },
    onError: (err: any) => {
      showError(err.message || 'Failed to recall plan');
    },
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleEditAssignment = (assignmentId: string) => {
    setEditingAssignmentId(assignmentId);
  };

  const handleCancelEdit = () => {
    setEditingAssignmentId(null);
  };

  const handleSaveAssignment = (assignmentId: string, substituteTeacherId: string | null) => {
    if (!substituteTeacherId) {
      showError('Please select a teacher');
      return;
    }
    updateAssignmentMutation.mutate({ id: assignmentId, substituteTeacherId });
  };

  const handleRemoveAssignment = (assignmentId: string) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Assignment',
      message: 'Are you sure you want to remove this assignment? The period will become unassigned.',
      severity: 'warning',
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        removeAssignmentMutation.mutate(assignmentId);
      },
    });
  };

  const handleAssignManually = (periodId: string) => {
    setAssigningPeriodId(periodId);
  };

  const handleCancelAssign = () => {
    setAssigningPeriodId(null);
  };

  const handleConfirmAssign = (periodId: string, substituteTeacherId: string | null) => {
    if (!substituteTeacherId) {
      showError('Please select a teacher');
      return;
    }
    
    // Find the unassigned assignment for this period
    const unassignedAssignment = unassignedAssignments.find((a) => a.period_id === periodId);
    if (!unassignedAssignment) {
      showError('Assignment not found');
      return;
    }

    updateAssignmentMutation.mutate({
      id: unassignedAssignment.id,
      substituteTeacherId,
    });
    setAssigningPeriodId(null);
  };

  const handlePostPlan = () => {
    // Unassigned periods confirmation (Req 6.4)
    if (unassignedCount > 0) {
      const unassignedList = unassignedAssignments
        .map(
          (a) =>
            `Period ${a.period.period_number} — ${a.class_section.class.class_name}${a.class_section.section} — ${a.subject.subject_name}`
        )
        .join(', ');

      setConfirmDialog({
        open: true,
        title: 'Unassigned Periods',
        message: `The following periods have no substitute assigned: ${unassignedList}. Post anyway?`,
        severity: 'warning',
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, open: false }));
          postPlanMutation.mutate();
        },
      });
      return;
    }

    postPlanMutation.mutate();
  };

  const handleDiscardDraft = () => {
    setConfirmDialog({
      open: true,
      title: 'Discard Draft',
      message: 'Are you sure you want to discard this draft plan? This action cannot be undone.',
      severity: 'warning',
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        discardDraftMutation.mutate();
      },
    });
  };

  const handleRerunAutoAssignment = () => {
    rerunMutation.mutate();
  };

  const handleRecallAndRedraft = () => {
    setConfirmDialog({
      open: true,
      title: 'Recall and Re-draft',
      message:
        'This will revert all posted assignments for this date to draft status and send cancellation notifications to all assigned substitutes. Continue?',
      severity: 'warning',
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, open: false }));
        recallMutation.mutate();
      },
    });
  };

  // Clear newly unassigned highlights after 5 seconds
  useEffect(() => {
    if (newlyUnassignedPeriodIds.size > 0) {
      const timer = setTimeout(() => setNewlyUnassignedPeriodIds(new Set()), 5000);
      return () => clearTimeout(timer);
    }
  }, [newlyUnassignedPeriodIds]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loadingAssignments) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (assignmentsError) {
    return (
      <Alert severity="error">
        Failed to load assignments: {(assignmentsError as Error).message}
      </Alert>
    );
  }

  if (!assignments || assignments.length === 0) {
    return (
      <Alert severity="info">
        No assignments found for this date. The engine has not yet been run.
      </Alert>
    );
  }

  const dayOfWeek = getDayOfWeek(targetDate);
  const formattedDate = formatDate(targetDate);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
      {/* Heading (Req 4.12) */}
      <Typography variant="h6" fontWeight="medium">
        Substitution Plan for {dayOfWeek}, {formattedDate}
      </Typography>

      {/* Standard user read-only banner */}
      {isStandardUser && (
        <Alert severity="info" icon={<LockIcon />}>
          View only — posting and editing require admin access.
        </Alert>
      )}

      {/* Concurrency lock banner (Req 21.2, 21.3, 21.7, 21.8) */}
      {/* TODO: Implement lock acquisition and display lock banner */}

      {/* All periods unassigned alert (Req 4.7) */}
      {totalPeriods > 0 && unassignedCount === totalPeriods && (
        <Alert severity="error" icon={<WarningIcon />}>
          No substitutes could be assigned for any period. Please assign manually or adjust
          substitution rules.
        </Alert>
      )}

      {/* Deactivated substitute warnings (Req 3.19, 3.20) */}
      {hasDeactivatedSubstitutes && isDraft && (
        <Alert severity="warning" icon={<PersonOffIcon />}>
          One or more assigned substitutes are no longer active. Please reassign those periods.
        </Alert>
      )}

      {hasDeactivatedSubstitutes && isPosted && (
        <Alert severity="warning" icon={<PersonOffIcon />}>
          One or more assigned substitutes are no longer active. Use "Recall and Re-draft" to
          correct the assignments.
        </Alert>
      )}

      {/* Absent substitute warning — substitute has their own absence on this date */}
      {hasAbsentSubstitutes && isDraft && (
        <Alert severity="error" icon={<WarningIcon />}>
          One or more assigned substitutes are themselves absent on this date. Please reassign
          those periods or use "Re-run Auto-Assignment".
        </Alert>
      )}
      {hasAbsentSubstitutes && isPosted && (
        <Alert severity="error" icon={<WarningIcon />}>
          One or more assigned substitutes are themselves absent on this date. Use "Recall and
          Re-draft" to correct the assignments.
        </Alert>
      )}

      {/* Half-day schedule notice (Req 13.5) */}
      {/* TODO: Detect half-day schedule and show notice banner */}

      {/* Day summary (Req 4.4) */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Total: ${totalPeriods}`} color="default" />
          <Chip label={`Assigned: ${assignedCount}`} color="success" />
          <Chip label={`Unassigned: ${unassignedCount}`} color="warning" />
        </Box>
      </Paper>

      {/* Assigned periods list (Req 4.1) */}
      {assignedAssignments.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
            Assigned Periods
          </Typography>
          <List disablePadding>
            {assignedAssignments.map((assignment) => {
              const isDeactivated =
                assignment.substitute_teacher && !assignment.substitute_teacher.is_active;
              const isEditing = editingAssignmentId === assignment.id;

              return (
                <Paper
                  key={assignment.id}
                  variant="outlined"
                  sx={{ mb: 1, p: 2 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Period {assignment.period.period_number} •{' '}
                        {formatTime(assignment.period.time_start)} –{' '}
                        {formatTime(assignment.period.time_end)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {assignment.class_section.class.class_name}
                        {assignment.class_section.section} • {assignment.subject.subject_name}
                      </Typography>

                      {/* Edit mode: show TeacherSelector */}
                      {isEditing ? (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TeacherSelector
                            value={assignment.substitute_teacher_id}
                            onChange={(teacherId) => {
                              if (teacherId) {
                                handleSaveAssignment(assignment.id, teacherId);
                              }
                            }}
                            targetDate={targetDate}
                            periodId={assignment.period_id}
                            size="small"
                            label="Select Substitute Teacher"
                            placeholder="Search by name..."
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleCancelEdit}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Typography variant="body2" color="text.primary">
                            Substitute: {assignment.substitute_teacher?.name || 'Unknown'}
                          </Typography>
                          {assignment.substitute_daily_count !== undefined && (
                            <Chip
                              label={`${assignment.substitute_daily_count} today`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                          {assignment.assignment_method === 'manual' && (
                            <Chip label="Manual" size="small" color="primary" variant="outlined" />
                          )}
                          {isDeactivated && (
                            <Tooltip title="Substitute no longer active — please reassign">
                              <Chip
                                icon={<PersonOffIcon fontSize="small" />}
                                label="Inactive"
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            </Tooltip>
                          )}
                          {assignment.substitute_teacher_id &&
                            absentTeacherIds.has(assignment.substitute_teacher_id) && (
                              <Tooltip title="This substitute is absent on this date — please reassign">
                                <Chip
                                  icon={<WarningIcon fontSize="small" />}
                                  label="Absent"
                                  size="small"
                                  color="error"
                                />
                              </Tooltip>
                            )}
                        </Box>
                      )}

                      {/* Notification status badge (Req 18.1–18.4) */}
                      {assignment.status === 'posted' && (
                        <Box sx={{ mt: 1 }}>
                          {/* TODO: Fetch notification status and show badge with resend button */}
                          <Chip
                            icon={<CheckCircleIcon fontSize="small" />}
                            label="Sent"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        </Box>
                      )}
                    </Box>

                    {/* Edit/Remove actions (only for draft, only for admins) */}
                    {isDraft && isAdmin && !isEditing && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit assignment">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditAssignment(assignment.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove assignment">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveAssignment(assignment.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </List>
        </Box>
      )}

      {/* Unassigned periods section (Req 11.1, 11.2, 11.4) */}
      {unassignedAssignments.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
            Unassigned Periods
          </Typography>
          <List disablePadding>
            {unassignedAssignments.map((assignment) => {
              const isNewlyUnassigned = newlyUnassignedPeriodIds.has(assignment.period_id);
              const isAssigning = assigningPeriodId === assignment.period_id;

              return (
                <Paper
                  key={assignment.id}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    p: 2,
                    bgcolor: isNewlyUnassigned ? 'warning.50' : 'background.paper',
                    borderColor: isNewlyUnassigned ? 'warning.main' : 'divider',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Period {assignment.period.period_number} •{' '}
                        {formatTime(assignment.period.time_start)} –{' '}
                        {formatTime(assignment.period.time_end)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {assignment.class_section.class.class_name}
                        {assignment.class_section.section} • {assignment.subject.subject_name}
                      </Typography>
                      {assignment.unassigned_reason && (
                        <Typography variant="caption" color="text.secondary">
                          Reason: {assignment.unassigned_reason.replace(/_/g, ' ')}
                        </Typography>
                      )}
                      {isNewlyUnassigned && (
                        <Chip
                          label="Newly Unassigned"
                          size="small"
                          color="warning"
                          sx={{ mt: 0.5 }}
                        />
                      )}

                      {/* Assign mode: show TeacherSelector */}
                      {isAssigning && (
                        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <TeacherSelector
                            value={null}
                            onChange={(teacherId) => {
                              if (teacherId) {
                                handleConfirmAssign(assignment.period_id, teacherId);
                              }
                            }}
                            targetDate={targetDate}
                            periodId={assignment.period_id}
                            size="small"
                            label="Select Substitute Teacher"
                            placeholder="Search by name..."
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={handleCancelAssign}
                            >
                              Cancel
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>

                    {/* Assign Manually button (only for draft, only for admins) */}
                    {isDraft && isAdmin && !isAssigning && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleAssignManually(assignment.period_id)}
                      >
                        Assign Manually
                      </Button>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </List>
        </Box>
      )}

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
        {isDraft && isAdmin && (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={postPlanMutation.isPending ? <CircularProgress size={16} /> : <SendIcon />}
              disabled={postPlanMutation.isPending || !canPost}
              onClick={handlePostPlan}
            >
              Post Plan
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={discardDraftMutation.isPending ? <CircularProgress size={16} /> : <DeleteIcon />}
              disabled={discardDraftMutation.isPending}
              onClick={handleDiscardDraft}
            >
              Discard Draft
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={rerunMutation.isPending ? <CircularProgress size={16} /> : <RefreshIcon />}
              disabled={rerunMutation.isPending}
              onClick={handleRerunAutoAssignment}
            >
              Re-run Auto-Assignment
            </Button>
          </>
        )}

        {isPosted && isAdmin && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={recallMutation.isPending ? <CircularProgress size={16} /> : <UndoIcon />}
            disabled={recallMutation.isPending}
            onClick={handleRecallAndRedraft}
          >
            Recall and Re-draft
          </Button>
        )}
      </Box>

      {/* Audit log collapsible section (Admin only) (Req 20.4) */}
      {isAdmin && auditLog && auditLog.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button
            onClick={() => setAuditLogExpanded(!auditLogExpanded)}
            endIcon={auditLogExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ mb: 1 }}
          >
            Audit Log ({auditLog.length} entries)
          </Button>
          <Collapse in={auditLogExpanded}>
            <Paper variant="outlined" sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
              <List disablePadding>
                {auditLog.map((entry, index) => (
                  <ListItem
                    key={entry.id}
                    disablePadding
                    sx={{
                      borderLeft: '2px solid',
                      borderColor: 'primary.main',
                      pl: 2,
                      mb: index < auditLog.length - 1 ? 2 : 0,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight="medium">
                          {entry.action.replace(/_/g, ' ')}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {format(parseISO(entry.created_at), 'MMM d, yyyy h:mm a')}
                            {entry.performed_by_name && ` • ${entry.performed_by_name}`}
                          </Typography>
                          {entry.details && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {JSON.stringify(entry.details, null, 2)}
                            </Typography>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Collapse>
        </Box>
      )}

      {/* Confirm dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        severity={confirmDialog.severity}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
}
